import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import axios, { CancelTokenSource } from 'axios'
import { Table } from 'components/Table'
import { Input } from 'components/Input'
import { Popup } from 'components/Popup'
import { Typography, TYPOGRAPHY_WEIGHT } from 'components/Typography'
import { Button, BUTTON_TYPES } from 'components/Button'
import { AddPopup } from './AddPopup/AddPopup'
import { CheckboxCell } from 'components/CheckboxCell/CheckboxCell'
import { setLoading } from 'redux/store/common/slice'
import { getAssignedCoUsers, getAssignedVendorsUsers } from './api'
import { columns } from './constants'
import { isTableHasParams } from 'helper/common'
import { BTN_TXT, PLACEHOLDERS } from 'constants/txt'
import { getUser } from 'redux/store/user/getters'
import { useDebounce } from 'hooks/hooks'
import { ReactComponent as Plus16 } from 'assets/svg/Plus16.svg'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { ReactComponent as Delete16 } from 'assets/svg/Delete16.svg'
import {
  THealthSystemTableRequestData,
  THealthSystemUsersParams
} from 'features/HealthSystemUsers/types'
import {
  TRFPUsersData,
  TRFPUsersProps,
  TRFPUsersFilters,
  TRFPUsersFiltersFunc
} from './types'
import './styles.scss'
import {
  deleteCoUsersOnRFP,
  deleteVendorUsersOnRFP
} from 'features/RFP/RFPDetails/api'
import { DEFAULT_PAGE } from 'components/Table/constants'
import { useRFPDetailsContext } from 'features/RFP/RFPDetails/Providers/RFPDetailsContextProvider'
import { ReactComponent as Close16 } from '../../../assets/svg/Clos16.svg'
import RadioGroup from '../../../components/RadioGroup/RadioGroup'
import useRouter from '../../../hooks/useRouter'
import { routes } from '../../../router'
import { useNavigate } from 'react-router'
import { USERS_PAGE_OPTIONS } from '../../../constants'
import { RFP_STATUSES } from '../constants'

export const Users = ({ disableActions }: TRFPUsersProps) => {
  const rfpDetailsContext = useRFPDetailsContext()
  const { data, canAddRfpUser, canAddVendorsUsers, canSeeHealthSystem } =
    rfpDetailsContext.state
  const { query } = useRouter()
  const navigate = useNavigate()
  const [activeSubTab, setActiveSubTab] = useState(
    query?.subtab || USERS_PAGE_OPTIONS[0].value
  )

  const isVendorTab = activeSubTab === USERS_PAGE_OPTIONS[1].value

  const [tableData, setTableData] = useState<TRFPUsersData[]>([])
  const [sortParams, setSortParams] = useState<THealthSystemTableRequestData>()
  const [filters, setFilters] = useState<TRFPUsersFilters>({})
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [firstLoad, setFirstLoad] = useState(true)
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false)
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const tableDataTokenRef = useRef<CancelTokenSource>()
  const [totalNumber, setTotalNumber] = useState(0)
  const user = useSelector(getUser)
  const dispatch = useDispatch()
  const { id } = useParams()
  const debouncedSearch = useDebounce(searchValue, 500)

  const handleTabs = (e) => {
    dispatch(setLoading(true))
    resetTableData()
    setActiveSubTab(e)
    navigate(`${routes.bidding}${routes.rfp}/${query.id}/${query.tab}/${e}`)
  }

  useEffect(() => {
    getTableData({ ...sortParams, ...filters, page: { ...DEFAULT_PAGE } })
    setPageInfo({ ...DEFAULT_PAGE })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, isVendorTab])

  const resetTableData = () => {
    setPageInfo({ ...DEFAULT_PAGE })
    setTableData([])
    setSearchValue('')
    setSelectedRowKeys([])
    setFilters({})
    setSortParams({})
    setTotalNumber(0)
  }

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])
  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: THealthSystemTableRequestData) => {
    dispatch(setLoading(true))
    if (!!tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: THealthSystemUsersParams = { ...filters }
    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    if (searchValue) {
      params.search = searchValue
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()
    const apiRequest = isVendorTab
      ? getAssignedVendorsUsers
      : getAssignedCoUsers
    apiRequest({
      id: id as string,
      data: {
        cancelToken: tableDataTokenRef.current?.token,
        params: params
      }
    })
      .then((resp) => {
        if (!resp.data?.results) {
          return
        }
        setTableData(
          resp.data.results.map((i) => ({
            ...i,
            key: i.uuid
          }))
        )
        setTotalNumber(resp.data.count)
        if (firstLoad) {
          setFirstLoad(false)
        }
      })
      .finally(() => dispatch(setLoading(false)))
  }

  const applyFilters: TRFPUsersFiltersFunc = (field, appliedFilters) => {
    const newFilters = { ...filters, [field]: appliedFilters }
    setFilters(newFilters)
    getTableData({ ...sortParams, filters: newFilters })
  }
  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }
  const handleSelectRow = (selectedRowKeys) =>
    setSelectedRowKeys(selectedRowKeys)

  const handleDeleteUsers = () => {
    dispatch(setLoading(true))
    const apiRequest = isVendorTab ? deleteVendorUsersOnRFP : deleteCoUsersOnRFP
    apiRequest(id as string, {
      users: selectedRowKeys
    })
      .then(() => {
        getTableData({ ...sortParams, filters })
        handleCloseDeletePopup()
      })
      .catch(() => dispatch(setLoading(false)))
    handleCloseDeletePopup()
  }
  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }
  const handleCloseDeletePopup = () => {
    setIsDeletePopupOpen(false)
    setSelectedRowKeys([])
  }

  const isCanEditRfpUsers = useMemo(
    () =>
      isVendorTab
        ? user.vendor &&
          canAddVendorsUsers &&
          data.status !== RFP_STATUSES.ARCHIVED &&
          data.status !== RFP_STATUSES.CANCELED
        : !user.vendor && canAddRfpUser && !disableActions,
    [
      isVendorTab,
      user.vendor,
      canAddVendorsUsers,
      data.status,
      canAddRfpUser,
      disableActions
    ]
  )

  return (
    <div className="table-wrapper hs-users-rfp">
      <div className="mb-24">
        <RadioGroup.Buttons
          options={USERS_PAGE_OPTIONS}
          defaultValue={activeSubTab}
          onChange={handleTabs}
        />
      </div>
      <div className="table-wrapper__header">
        {!!selectedRowKeys.length ? (
          <Typography.Body1
            className="table-wrapper__select-title"
            weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
          >
            Selected {selectedRowKeys.length} of {totalNumber}
          </Typography.Body1>
        ) : (
          <Input
            className="allow-clear"
            propsInput={{
              allowClear: { clearIcon: <Close16 /> },
              className: 'rfp-hs-users_search',
              placeholder: PLACEHOLDERS.SEARCH,
              prefix: <Search16 />,
              value: searchValue,
              onChange: (e) => setSearchValue(e.target.value)
            }}
          />
        )}
        <div className="table-wrapper__header_buttons">
          {isCanEditRfpUsers && !selectedRowKeys.length && (
            <Button
              type={BUTTON_TYPES.PRIMARY}
              icon={<Plus16 />}
              onClick={() => setIsAddPopupOpen(true)}
              upperCase
            >
              {BTN_TXT.ADD_USER}
            </Button>
          )}
          {selectedRowKeys.length > 0 && (
            <Button
              type={BUTTON_TYPES.PRIMARY}
              onClick={() => setIsDeletePopupOpen(true)}
              icon={<Delete16 />}
              upperCase
              danger
            >
              Delete
            </Button>
          )}
        </div>
      </div>
      <Table<TRFPUsersData>
        dataSource={tableData}
        columns={columns({
          applyFilters,
          filters,
          community: user.community as string,
          isVendor: Boolean(user.vendor),
          isVendorTab,
          canSeeHealthSystem
        })}
        scroll={{ scrollToFirstRowOnChange: true, x: 'max-content' }}
        className="hs-users-table"
        pageSize={pageInfo.pageSize}
        pagination={{
          pageSize: pageInfo.pageSize,
          current: pageInfo.pageNumber,
          total: totalNumber
        }}
        onChangePage={handleChangePageSize}
        onChange={handleTableChange}
        hasSearchOrFilters={isTableHasParams<TRFPUsersFilters>(
          debouncedSearch,
          filters
        )}
        createButton={
          isCanEditRfpUsers
            ? {
                buttonText: BTN_TXT.ADD_USER,
                availability: true,
                action: () => setIsAddPopupOpen(true)
              }
            : undefined
        }
        rowSelection={
          isCanEditRfpUsers
            ? {
                selectedRowKeys,
                onChange: handleSelectRow,
                renderCell: (_, record, __, node) =>
                  record.can_be_removed ? (
                    node
                  ) : (
                    <CheckboxCell tooltipText="User cannot be deleted from the list">
                      {node}
                    </CheckboxCell>
                  ),
                getCheckboxProps: (record) => ({
                  disabled: !record.can_be_removed
                })
              }
            : undefined
        }
      />
      {isAddPopupOpen && (
        <AddPopup
          isAddPopupOpen={isAddPopupOpen}
          setIsAddPopupOpen={setIsAddPopupOpen}
          updateAssignedValues={() =>
            getTableData({ ...sortParams, ...filters })
          }
          isVendorTab={isVendorTab}
          canSeeHealthSystem={canSeeHealthSystem}
        />
      )}
      <Popup
        visible={isDeletePopupOpen}
        onCancel={handleCloseDeletePopup}
        title={`Delete user${selectedRowKeys.length > 1 ? 's' : ''}?`}
        width={442}
        footer={
          <>
            <Button
              type={BUTTON_TYPES.DEFAULT}
              onClick={handleCloseDeletePopup}
              upperCase
            >
              {BTN_TXT.CANCEL}
            </Button>
            <Button
              type={BUTTON_TYPES.PRIMARY}
              onClick={handleDeleteUsers}
              upperCase
              danger
            >
              {BTN_TXT.DELETE}
            </Button>
          </>
        }
      >
        <Typography.Body1>
          {selectedRowKeys.length} user{selectedRowKeys.length > 1 ? 's' : ''}{' '}
          will be removed from RFP.
          <br />
          Do you really want to delete user
          {selectedRowKeys.length > 1 ? 's' : ''} from the list?
        </Typography.Body1>
      </Popup>
    </div>
  )
}
